if (!customElements.get('sticky-atc')) {
  class AwStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.dataset.awInitialized === 'true') return;
      this.dataset.awInitialized = 'true';

      this.sectionId = this.dataset.section;
      this.mainProduct = document.getElementById(`MainProduct-${this.sectionId}`);
      this.primaryButton =
        this.mainProduct?.querySelector('product-form button[name="add"]') ||
        this.mainProduct?.querySelector('.product-form__submit');

      this.addEventListener('click', this.handleClick.bind(this));
      this.initVariantSync();

      if (this.dataset.afterScroll === 'true') {
        this.updateVisibility = this.updateVisibility.bind(this);
        const anchor = this.primaryButton || this.mainProduct;
        if (anchor && 'IntersectionObserver' in window) {
          this.visibilityObserver = new IntersectionObserver(this.updateVisibility, {
            threshold: 0,
          });
          this.visibilityObserver.observe(anchor);
        } else {
          window.addEventListener('scroll', this.updateVisibility, { passive: true });
          window.addEventListener('resize', this.updateVisibility, { passive: true });
          requestAnimationFrame(this.updateVisibility);
        }
      } else {
        this.classList.add('is-visible');
      }
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      if (!this.updateVisibility) return;
      window.removeEventListener('scroll', this.updateVisibility);
      window.removeEventListener('resize', this.updateVisibility);
    }

    updateVisibility() {
      const anchor = this.primaryButton || this.mainProduct;
      if (!anchor) {
        this.classList.toggle('is-visible', window.scrollY > 400);
        return;
      }

      const rect = anchor.getBoundingClientRect();
      this.classList.toggle('is-visible', rect.bottom <= 0);
    }

    // --- Variant picker sync -------------------------------------------------
    // The sticky bar renders a <secondary-variant-select(-separate)> (Shrine
    // markup, no JS behind it on this theme). Mirror its selection into the main
    // <variant-selects> (snippets/product-variant-picker.liquid) and dispatch a
    // `change` there so global.js publishes optionValueSelectionChange and
    // product-info.js updates price/media/variant id. Also mirror the main
    // picker back into the sticky selects so both always agree.
    initVariantSync() {
      this.stickyPicker = this.querySelector('secondary-variant-select, secondary-variant-select-separate');
      if (!this.stickyPicker || !this.mainProduct) return;

      this.stickyVariants = null;
      try {
        const json = this.stickyPicker.querySelector('script[type="application/json"]');
        const parsed = json ? JSON.parse(json.textContent) : null;
        if (Array.isArray(parsed)) this.stickyVariants = parsed;
      } catch (error) {
        this.stickyVariants = null;
      }

      this.stickyPicker.addEventListener('change', (event) => {
        if (this.syncingVariants || event.target.tagName !== 'SELECT') return;
        this.syncStickyToMain();
      });

      // Delegated on the section: product-info.js replaces <variant-selects> on every render.
      this.mainProduct.addEventListener('change', (event) => {
        if (this.syncingVariants || !event.target.closest('variant-selects')) return;
        this.syncMainToSticky();
      });
    }

    get mainVariantSelects() {
      return this.mainProduct?.querySelector('variant-selects') || null;
    }

    getMainOptionGroups(variantSelects) {
      return Array.from(variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'));
    }

    getStickySelectedOptions() {
      const combined = this.stickyPicker.querySelector('.sticky-atc__variant-select');
      if (combined) {
        const variant = this.stickyVariants?.[combined.selectedIndex];
        if (variant && Array.isArray(variant.options)) return variant.options;
        const raw = combined.selectedOptions[0]?.dataset.options;
        return raw ? raw.split(',') : null;
      }
      const selects = Array.from(this.stickyPicker.querySelectorAll('select'));
      return selects.length ? selects.map((select) => select.value) : null;
    }

    syncStickyToMain() {
      const variantSelects = this.mainVariantSelects;
      const values = this.getStickySelectedOptions();
      if (!variantSelects || !values) return;

      let changedInput = null;
      this.getMainOptionGroups(variantSelects).forEach((group, index) => {
        const value = values[index];
        if (value == null) return;

        const select = group.querySelector('select');
        if (select) {
          const option = Array.from(select.options).find((item) => item.value === value);
          if (!option || option.selected) return;
          // Keep the `selected` attribute in step: global.js reads `option[selected]`.
          Array.from(select.options).forEach((item) => item.removeAttribute('selected'));
          option.setAttribute('selected', 'selected');
          select.value = value;
          changedInput = select;
          return;
        }

        const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((item) => item.value === value);
        if (!radio || radio.checked) return;
        radio.checked = true;
        changedInput = radio;
      });

      if (!changedInput) return;
      this.syncingVariants = true;
      try {
        changedInput.dispatchEvent(new Event('change', { bubbles: true }));
      } finally {
        this.syncingVariants = false;
      }
    }

    syncMainToSticky() {
      const variantSelects = this.mainVariantSelects;
      if (!variantSelects || !this.stickyPicker) return;

      const values = this.getMainOptionGroups(variantSelects).map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        return group.querySelector('input[type="radio"]:checked')?.value ?? null;
      });
      if (!values.length) return;

      this.syncingVariants = true;
      try {
        const combined = this.stickyPicker.querySelector('.sticky-atc__variant-select');
        if (combined) {
          const matches = (options) =>
            Array.isArray(options) &&
            options.length === values.length &&
            options.every((option, index) => option === values[index]);
          let index = this.stickyVariants ? this.stickyVariants.findIndex((variant) => matches(variant.options)) : -1;
          if (index < 0) {
            const joined = values.join(',');
            index = Array.from(combined.options).findIndex((option) => option.dataset.options === joined);
          }
          if (index >= 0 && index !== combined.selectedIndex) combined.selectedIndex = index;
        } else {
          Array.from(this.stickyPicker.querySelectorAll('select')).forEach((select, index) => {
            const value = values[index];
            if (value == null || select.value === value) return;
            if (Array.from(select.options).some((option) => option.value === value)) select.value = value;
          });
        }
      } finally {
        this.syncingVariants = false;
      }
    }

    handleClick(event) {
      const scrollButton = event.target.closest('.sticky-atc__scroll-btn');
      if (scrollButton) {
        event.preventDefault();
        const selector = this.dataset.scrollDestination;
        let destination = null;

        if (selector) {
          try {
            destination = document.querySelector(selector);
          } catch (error) {
            destination = null;
          }
        }

        destination ||= this.primaryButton;
        destination?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const addButton = event.target.closest('.main-product-atc');
      if (!addButton) return;

      event.preventDefault();
      if (!this.primaryButton || this.primaryButton.disabled) return;
      this.primaryButton.click();
    }
  }

  customElements.define('sticky-atc', AwStickyAtc);
}
